import {Capacitor, CapacitorHttp, type HttpOptions, type HttpResponse} from '@capacitor/core'
//import {Directory, Filesystem} from '@capacitor/filesystem'
import {Share} from '@capacitor/share'
import {useAuthStore} from '@/stores/auth'
import {Logger} from './logger'

export interface WrapperHttpOptions {
    method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH'
    headers?: Record<string, string>
    body?: any
    timeout?: number
}

export interface HttpConfig {
    baseURL?: string
    defaultHeaders?: Record<string, string>
    timeout?: number
}

class HttpClient {
    private config: HttpConfig = {}

    configure(config: HttpConfig) {
        this.config = {...this.config, ...config}
    }

    async request<T = any>(url: string, options: WrapperHttpOptions = {}): Promise<T> {
        const requestOptions = this.prepareRequest(url, options)

        Logger.debug('Making request to:', requestOptions)

        const response = await CapacitorHttp.request(requestOptions)

        Logger.debug('Response:', response)

        if (response.status >= 400) {
            throw new Error(response.data.message || `HTTP ${response.status}`)
        }

        return response.data
    }

    async get<T = any>(url: string, options: Omit<WrapperHttpOptions, 'method'> = {}): Promise<T> {
        return this.request<T>(url, {...options, method: 'GET'})
    }

    async post<T = any>(url: string, options: Omit<WrapperHttpOptions, 'method'> = {}): Promise<T> {
        return this.request<T>(url, {...options, method: 'POST'})
    }

    async put<T = any>(url: string, options: Omit<WrapperHttpOptions, 'method'> = {}): Promise<T> {
        return this.request<T>(url, {...options, method: 'PUT'})
    }

    async delete<T = any>(url: string, options: Omit<WrapperHttpOptions, 'method'> = {}): Promise<T> {
        return this.request<T>(url, {...options, method: 'DELETE'})
    }

    async patch<T = any>(url: string, options: Omit<WrapperHttpOptions, 'method'> = {}): Promise<T> {
        return this.request<T>(url, {...options, method: 'PATCH'})
    }

    async downloadFile(url: string, filename: string, options: Omit<WrapperHttpOptions, 'method'> = {}): Promise<{ success: boolean; path?: string; error?: string }> {
        try {
            const requestOptions = this.prepareRequest(url, {...options, method: 'GET'})
            requestOptions.responseType = 'blob'

            const response: HttpResponse = await CapacitorHttp.request(requestOptions)

            if (response.status >= 400) {
                throw new Error(`HTTP ${response.status}: ${response.data}`)
            }

            const fileName = filename || 'download'

            if (!Capacitor.isNativePlatform()) {
                return this.handleWebDownload(response.data, fileName)
            }

        } catch (error) {
            Logger.error('Download failed:', error)
            return {
                success: false,
                error: error instanceof Error ? error.message : 'Unknown download error'
            }
        }
    }

    private prepareRequest(uri: string, options: WrapperHttpOptions): HttpOptions {
        const fullUrl = uri.indexOf('http') === 0 ? uri : `${this.config.baseURL || ''}${uri}`
        const headers = this.buildHeaders(options.headers)
        const method = options.method || 'GET'
        const timeout = options.timeout || this.config.timeout || 30000

        const requestOptions: HttpOptions = {
            url: fullUrl,
            method,
            headers,
            connectTimeout: timeout,
            readTimeout: timeout
        }

        if (options.body) {
            requestOptions.data = this.serializeBody(options.body)
            if (!headers['Content-Type']) {
                headers['Content-Type'] = 'application/json'
            }
        }

        return requestOptions
    }

    private buildHeaders(optionHeaders: Record<string, string> = {}): Record<string, string> {
        const headers = {
            ...this.config.defaultHeaders,
            ...optionHeaders
        }

        // Add Bearer token if available
        try {
            const authStore = useAuthStore()
            if (authStore.token && authStore.logged && !headers['Authorization']) {
                headers['Authorization'] = `Bearer ${authStore.token}`
                headers['X-Api-Token'] = `Bearer ${authStore.token}`
            }
        } catch (error) {
            Logger.debug('Auth store not available:', error)
        }

        return headers
    }

    private serializeBody(body: any): string | FormData {
        if (!body) return ''
        if (body instanceof FormData) return body
        if (typeof body === 'string') return body
        return JSON.stringify(body)
    }

    private async handleNativeDownload(data: any, fileName: string): Promise<{ success: boolean; path?: string }> {
        const result = await Filesystem.writeFile({
            path: fileName,
            data: data,
            directory: Directory.Documents
        })

        try {
            await Share.share({
                title: 'Download Complete',
                text: `${fileName} has been downloaded to Documents`,
                url: result.uri,
                dialogTitle: 'File Downloaded Successfully'
            })
        } catch (shareError) {
            Logger.error('Share dialog not available or cancelled:', shareError)
        }

        return {success: true, path: result.uri}
    }

    private handleWebDownload(data: any, fileName: string): { success: boolean; path?: string } {
        // Capacitor HTTP returns base64 data for blob responseType
        let binaryData: any = data

        if (typeof data === 'string') {
            // If data is base64 string, decode it to binary
            try {
                const binaryString = atob(data)
                const bytes = new Uint8Array(binaryString.length)
                for (let i = 0; i < binaryString.length; i++) {
                    bytes[i] = binaryString.charCodeAt(i)
                }
                binaryData = bytes
            } catch (e) {
                // If atob fails, treat as regular string
                Logger.error('Failed to decode base64 data:', e)
                binaryData = data
            }
        }

        const blob = new Blob([binaryData])
        const blobUrl = window.URL.createObjectURL(blob)

        const a = document.createElement('a')
        a.style.display = 'none'
        a.href = blobUrl
        a.download = fileName
        document.body.appendChild(a)
        a.click()

        document.body.removeChild(a)
        window.URL.revokeObjectURL(blobUrl)

        return {success: true, path: blobUrl}
    }
}

export const http = new HttpClient()

http.configure({
    baseURL: import.meta.env.VITE_API_URL || 'http://localhost',
    defaultHeaders: {
        'Accept': 'application/json',
        'Content-Type': 'application/json'
    },
    timeout: 30000
})
